import { Alert, Button, Group, Modal, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { IME_APLIKACIJE } from '../ime';

/**
 * Pošiljanje nove povratne informacije (kot FeedbackDialog v Swingu): POST /api/povratne-informacije. Odpre se z
 * zaslona Povratne informacije (nato {@code poslano} odpre pogovor) ali iz Analiz (samo obvestilo, kje bo odgovor).
 */
export function NovaPovratnaInformacija({
  odprto,
  zapri,
  poslano,
}: {
  odprto: boolean;
  zapri: () => void;
  poslano?: (p: Shema['PovratnaInformacija']) => void;
}) {
  const odjemalec = useQueryClient();
  const [besedilo, setBesedilo] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [napakaPolja, setNapakaPolja] = useState<string | null>(null);
  const [posiljam, setPosiljam] = useState(false);

  const poslji = async () => {
    setNapaka(null);
    setNapakaPolja(null);
    setPosiljam(true);
    try {
      const p = await podatki(api.POST('/api/povratne-informacije', { body: { besedilo } }));
      setBesedilo('');
      await odjemalec.invalidateQueries({ queryKey: ['povratne'] });
      zapri();
      notifications.show({
        color: 'green',
        title: 'Hvala!',
        message: poslano
          ? 'Povratna informacija je poslana.'
          : 'Povratna informacija je poslana. Odgovor boste videli na začetnem zaslonu pod Povratne informacije.',
      });
      poslano?.(p);
    } catch (e) {
      if (e instanceof NapakaApi && e.polja.besedilo) {
        setNapakaPolja(e.polja.besedilo);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setPosiljam(false);
    }
  };

  return (
    <Modal opened={odprto} onClose={zapri} title="Povratne informacije" size="lg" radius="lg">
      <Text fw={700} mb={4}>
        Pozdravljeni,
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        zahvaljujemo se vam za uporabo naše aplikacije {IME_APLIKACIJE}, veseli pa bomo tudi vsake povratne informacije,
        tako pozitivne kot negativne. Pričakujemo tudi vaše predloge, ki bi pripomogli pri vašem in našem delu. Odgovor
        boste videli pod Povratne informacije in ga prejeli po e-pošti.
      </Text>
      <Textarea
        aria-label="Komentar"
        value={besedilo}
        onChange={(e) => setBesedilo(e.currentTarget.value)}
        error={napakaPolja}
        autosize
        minRows={6}
        maxRows={14}
        data-autofocus
      />
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={zapri}>
          Prekliči
        </Button>
        <Button onClick={() => void poslji()} loading={posiljam}>
          Pošlji
        </Button>
      </Group>
    </Modal>
  );
}
